/**
 * Maps the backend `discardReason` contract onto display text.
 *
 * Lives in its own file rather than helper.ts because calculate.model.ts consumes the
 * DiscardReasonSource type, and helper.ts already imports from the models barrel.
 */

/** Where a displayed discard reason came from. */
export type DiscardReasonSource = 'auto' | 'manual' | 'outlier' | 'none';

/** The literal the backend uses for a replicate the operator discarded by hand. */
export const MANUAL_DISCARD_REASON = 'Manual';

/** Display text for a manual discard. */
export const MANUAL_DISCARD_DISPLAY = 'Manually discarded';

/** Display text for a replicate excluded from the mean as a statistical outlier. */
export const OUTLIER_DISPLAY = 'Outlier';

/** Placeholder shown when there is no reason to display. */
export const NO_DISCARD_REASON_DISPLAY = '-';

export interface FormattedDiscardReason {
  text: string;
  source: DiscardReasonSource;
}

/**
 * Resolves the Discard Reason cell for one replicate.
 *
 * A replicate can be discarded without carrying a reason, which is why the "no reason" case is keyed
 * off the reason itself rather than off isDiscarded alone. Outliers are modelled separately from
 * isDiscarded on the backend (a discarded replicate is never evaluated as one), so they are only
 * reported when the caller passes isOutlier.
 *
 * @param isDiscarded Whether the replicate was excluded from the math.
 * @param rawReason The backend `discardReason` value.
 * @param isOutlier Whether the replicate was excluded from the mean as an outlier.
 * @returns The text to render and the source it came from.
 */
export function formatDiscardReason(
  isDiscarded: boolean | undefined | null,
  rawReason: string | null | undefined,
  isOutlier?: boolean | null
): FormattedDiscardReason {
  if (!isDiscarded && isOutlier) {
    return { text: OUTLIER_DISPLAY, source: 'outlier' };
  }
  const trimmed = (rawReason ?? '').trim();
  if (!isDiscarded || !trimmed || trimmed.toLowerCase() === 'none') {
    return { text: NO_DISCARD_REASON_DISPLAY, source: 'none' };
  }
  if (trimmed === MANUAL_DISCARD_REASON) {
    return { text: MANUAL_DISCARD_DISPLAY, source: 'manual' };
  }
  return { text: trimmed, source: 'auto' };
}

/** True when the replicate was discarded automatically, i.e. not by the operator. */
export function isAutoDiscardReason(
  isDiscarded: boolean | undefined | null,
  rawReason: string | null | undefined
): boolean {
  return formatDiscardReason(isDiscarded, rawReason).source === 'auto';
}
